// Frontend-v2 canvas board contract — the app's OWN state (a user's arranged
// dashboard), stored in DynamoDB scoped to the logged-in user, never the
// read-only Catalyst RDS (invariant §12.11).
//
// This is DELIBERATELY separate from the v1 `layout.ts` contract. v2's model is
// different: ONE freeform canvas per user (no leagues/players sections), and
// each widget carries its OWN players + per-widget state — not just a position.
// Keeping it a parallel contract means the live v1 `/layouts` demo keeps working
// untouched while v2 is built beside it (see docs/frontend-v2/).
//
// Pure: no AWS, no DOM. Shared by app-web (arranging widgets) and app-api
// (validating a board on write) so both run the same geometry + validation.

import type { GridPosition } from "../grid/grid.js";
import type { ParseResult } from "../layout/layout.js";

/**
 * The minimum a widget needs to identify a player it is showing. Identity is the
 * THREE ids — a player can be in a league AND a cup in the same season, so
 * playerId + season alone is ambiguous; competition disambiguates it. `seasonId`
 * / `competitionId` are null when a ref arrives without them (the frontend then
 * resolves the player's most-recent). `displayName` is a cached label for chips
 * / tooltips ONLY — never used for lookup or dedup (names collide; ids don't).
 */
export interface PlayerRef {
  playerId: number;
  competitionId: number | null;
  seasonId: number | null;
  displayName: string;
}

/** One placed widget on the v2 canvas: where it sits, who it shows, and its own
 *  opaque per-widget state (season overrides, filters, colors, …). The backend
 *  treats `widgetKey` and `state` as opaque — the widget registry is the
 *  frontend's, so the backend never needs a redeploy to add a widget. */
export interface CanvasWidget {
  widgetKey: string;
  position: GridPosition;
  players: PlayerRef[];
  state: Record<string, unknown>;
}

/** A user's whole canvas — the GET/PUT /canvas response shape. */
export interface CanvasBoard {
  widgets: CanvasWidget[];
  /** ISO timestamp of the last save, or null if never saved. */
  updatedAt: string | null;
}

/** Body of PUT /canvas. */
export interface CanvasUpdate {
  widgets: CanvasWidget[];
}

// Guard rails so one board can't grow unbounded (a DynamoDB item is capped at
// 400KB; these keep us comfortably under it and fail fast on a client bug).
export const MAX_CANVAS_WIDGETS = 50;
export const MAX_PLAYERS_PER_WIDGET = 5; // the prototype's compare cap
export const MAX_CANVAS_WIDGET_KEY_LEN = 100;
export const MAX_DISPLAY_NAME_LEN = 120;
/** Freeform canvas is not column-capped like v1's 12-col grid, but positions are
 *  still bounded so a stored board stays sane. Generous — the canvas grows to fit. */
export const MAX_CANVAS_COLUMNS = 96;
export const MAX_CANVAS_ROWS = 400;
/** Per-widget opaque state blob cap (serialized). Keeps a runaway widget from
 *  blowing the item size; 8KB is ample for filters/season/colors. */
export const MAX_STATE_BYTES = 8 * 1024;

/**
 * Clamp a freeform position to whole, non-negative, in-bounds cells. Unlike v1's
 * `clampToGrid` (which caps width to a fixed 12 columns), this only bounds the
 * canvas generously — the width is not forced to a column count. Returns a new
 * object; the input is not mutated. Fail-safe geometry, not a fallback for a real
 * error — drag/resize and untrusted boards can produce fractional/out-of-bounds
 * coords, and everything downstream can then assume a valid position.
 */
export function clampFreeform(pos: GridPosition): GridPosition {
  const w = Math.min(Math.max(1, Math.floor(pos.w)), MAX_CANVAS_COLUMNS);
  const h = Math.min(Math.max(1, Math.floor(pos.h)), MAX_CANVAS_ROWS);
  const x = Math.min(Math.max(0, Math.floor(pos.x)), MAX_CANVAS_COLUMNS - w);
  const y = Math.min(Math.max(0, Math.floor(pos.y)), MAX_CANVAS_ROWS - h);
  return { x, y, w, h };
}

/** A finite number, or an error. Rejects NaN/Infinity/strings/booleans. */
function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** Validate one PlayerRef from untrusted input. `competitionId`/`seasonId` may be
 *  null (unresolved), but when present must be finite numbers. */
function parsePlayerRef(raw: unknown): ParseResult<PlayerRef> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return { ok: false, error: "each player must be an object" };
  }
  const r = raw as Record<string, unknown>;
  if (!isFiniteNumber(r.playerId)) {
    return { ok: false, error: "player.playerId must be a finite number" };
  }
  for (const k of ["competitionId", "seasonId"] as const) {
    if (r[k] !== null && !isFiniteNumber(r[k])) {
      return { ok: false, error: `player.${k} must be a finite number or null` };
    }
  }
  const name = typeof r.displayName === "string" ? r.displayName.trim() : "";
  if (name.length === 0) {
    return { ok: false, error: "player.displayName must be a non-empty string" };
  }
  if (name.length > MAX_DISPLAY_NAME_LEN) {
    return { ok: false, error: `player.displayName must be ${MAX_DISPLAY_NAME_LEN} characters or fewer` };
  }
  return {
    ok: true,
    value: {
      playerId: r.playerId as number,
      competitionId: (r.competitionId as number | null) ?? null,
      seasonId: (r.seasonId as number | null) ?? null,
      displayName: name,
    },
  };
}

/** A grid position from untrusted input: all four fields finite numbers, then
 *  freeform-clamped. Bounds/whole-cell fixing is `clampFreeform`'s job. */
function parsePosition(raw: unknown): ParseResult<GridPosition> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return { ok: false, error: "widget.position must be an object { x, y, w, h }" };
  }
  const r = raw as Record<string, unknown>;
  for (const k of ["x", "y", "w", "h"] as const) {
    if (!isFiniteNumber(r[k])) {
      return { ok: false, error: `widget.position.${k} must be a finite number` };
    }
  }
  return {
    ok: true,
    value: clampFreeform({ x: r.x as number, y: r.y as number, w: r.w as number, h: r.h as number }),
  };
}

/** Validate ONE canvas widget from untrusted input, clamping geometry and
 *  bounding players + state size. `widgetKey`/`state` are opaque to the backend. */
function parseCanvasWidget(raw: unknown): ParseResult<CanvasWidget> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return { ok: false, error: "each widget must be an object" };
  }
  const w = raw as Record<string, unknown>;

  const widgetKey = w.widgetKey;
  if (typeof widgetKey !== "string" || widgetKey.trim() === "") {
    return { ok: false, error: "widget.widgetKey must be a non-empty string" };
  }
  if (widgetKey.length > MAX_CANVAS_WIDGET_KEY_LEN) {
    return { ok: false, error: `widget.widgetKey must be ${MAX_CANVAS_WIDGET_KEY_LEN} characters or fewer` };
  }

  const pos = parsePosition(w.position);
  if (!pos.ok) return pos;

  if (!Array.isArray(w.players)) {
    return { ok: false, error: "widget.players must be an array" };
  }
  if (w.players.length > MAX_PLAYERS_PER_WIDGET) {
    return { ok: false, error: `widget.players must be ${MAX_PLAYERS_PER_WIDGET} or fewer` };
  }
  const players: PlayerRef[] = [];
  for (const p of w.players) {
    const parsed = parsePlayerRef(p);
    if (!parsed.ok) return parsed;
    players.push(parsed.value);
  }

  // State is opaque, but must be a plain object and within the size cap.
  const state = w.state;
  if (typeof state !== "object" || state === null || Array.isArray(state)) {
    return { ok: false, error: "widget.state must be an object" };
  }
  let stateSize: number;
  try {
    stateSize = JSON.stringify(state).length;
  } catch {
    return { ok: false, error: "widget.state must be JSON-serializable" };
  }
  if (stateSize > MAX_STATE_BYTES) {
    return { ok: false, error: `widget.state must be ${MAX_STATE_BYTES} bytes or fewer` };
  }

  return {
    ok: true,
    value: { widgetKey, position: pos.value, players, state: state as Record<string, unknown> },
  };
}

/**
 * Validate + clamp a raw PUT /canvas body into `{ widgets }`, else an error
 * (→ 400). STRICT: any malformed widget fails the whole write (fail fast,
 * §12.2) — a client bug should surface, not silently drop a widget. Pure —
 * unit-tested without DynamoDB.
 */
export function parseCanvasBody(raw: string | null | undefined): ParseResult<CanvasUpdate> {
  if (!raw) return { ok: false, error: "request body is required" };
  let body: unknown;
  try {
    body = JSON.parse(raw);
  } catch {
    return { ok: false, error: "body must be valid JSON" };
  }
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    return { ok: false, error: "body must be a JSON object { widgets: [...] }" };
  }
  const widgets = (body as Record<string, unknown>).widgets;
  if (!Array.isArray(widgets)) {
    return { ok: false, error: "widgets must be an array" };
  }
  if (widgets.length > MAX_CANVAS_WIDGETS) {
    return { ok: false, error: `widgets must be ${MAX_CANVAS_WIDGETS} or fewer` };
  }
  const out: CanvasWidget[] = [];
  for (const wRaw of widgets) {
    const parsed = parseCanvasWidget(wRaw);
    if (!parsed.ok) return parsed;
    out.push(parsed.value);
  }
  return { ok: true, value: { widgets: out } };
}

/**
 * Coerce stored widgets (an `unknown` read back from DynamoDB) into a clean
 * `CanvasWidget[]`. LENIENT — unlike the write path, a malformed widget is
 * DROPPED, not rejected: a GET must never 500 on an old/corrupt board. Good
 * widgets are re-clamped. Pure — unit-tested.
 */
export function sanitizeCanvasWidgets(raw: unknown): CanvasWidget[] {
  if (!Array.isArray(raw)) return [];
  const out: CanvasWidget[] = [];
  for (const wRaw of raw) {
    const parsed = parseCanvasWidget(wRaw);
    if (parsed.ok) out.push(parsed.value);
  }
  return out;
}
